<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useAttrs, useId, watch } from 'vue';
import {
  SEARCH_SELECT_MAX_VISIBLE,
  filterOptions,
  nextActiveIndex,
  type SearchOption,
} from '../searchSelect';

/**
 * Sélecteur avec recherche (lot N), à la place d'un `<select>` dès qu'une liste peut être longue (clients,
 * devis, projets…). Motif ARIA « combobox » + « listbox » : on tape pour filtrer (sans tenir
 * compte de la casse ni des accents, texte secondaire compris), ↑/↓ pour parcourir, Entrée pour choisir,
 * Échap pour fermer, et la souris. `v-model` : la valeur choisie (`null` = rien). La liste s'ouvre au-dessus
 * du reste de la page (`Teleport`), pour ne pas être coupée par une carte, un tableau ou une fenêtre.
 *
 * `testid` et les autres attributs passés vont sur le champ ; `class` et `style` sur le conteneur.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    modelValue: string | null;
    options: readonly SearchOption[];
    /**
     * Nom accessible du champ et de la liste, à toujours donner (`aria-label="Client"`) : il n'y a pas toujours
     * de `<label>`. Facultatif pour le contrôle des types seulement (vue-tsc ne rapproche pas `aria-label` de
     * la prop), Vue le range bien dans cette prop.
     */
    ariaLabel?: string;
    placeholder?: string;
    disabled?: boolean;
    /** Bouton « Effacer » quand une valeur est choisie. */
    clearable?: boolean;
    /** Identifiant du champ (pour un `<label for>`). */
    id?: string;
    emptyText?: string;
    /** `data-testid` du champ (tests Playwright). */
    testid?: string;
  }>(),
  {
    ariaLabel: 'Rechercher',
    placeholder: 'Rechercher…',
    disabled: false,
    clearable: true,
    id: undefined,
    emptyText: 'Aucun résultat',
    testid: undefined,
  },
);
const emit = defineEmits<{
  'update:modelValue': [value: string | null];
  change: [value: string | null];
}>();

const attrs = useAttrs();
const rootAttrs = computed(() => ({ class: attrs.class, style: attrs.style }));
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const uid = useId();
const inputId = computed(() => props.id ?? `ss-${uid}`);
const listId = `ss-list-${uid}`;
const optionId = (index: number) => `ss-opt-${uid}-${index}`;

const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const list = ref<HTMLElement | null>(null);
const open = ref(false);
/** Vrai dès qu'on tape : le champ montre la recherche au lieu du libellé choisi. */
const editing = ref(false);
const query = ref('');
const active = ref(-1);

const selected = computed(() => props.options.find((o) => o.value === props.modelValue) ?? null);
const inputText = computed(() => (editing.value ? query.value : (selected.value?.label ?? '')));
const filtered = computed(() =>
  editing.value ? filterOptions(props.options, query.value) : [...props.options],
);
const visible = computed(() => filtered.value.slice(0, SEARCH_SELECT_MAX_VISIBLE));
const hidden = computed(() => filtered.value.length - visible.value.length);
const status = computed(() =>
  !open.value
    ? ''
    : filtered.value.length === 0
      ? props.emptyText
      : `${filtered.value.length} résultat${filtered.value.length > 1 ? 's' : ''}`,
);

// ---------------------------------------------------------------- Position de la liste (fixe, sous le champ)
const position = ref<Record<string, string>>({});
function place(): void {
  const el = root.value;
  if (!el || typeof window === 'undefined') return;
  const rect = el.getBoundingClientRect();
  const width = Math.min(Math.max(rect.width, 220), window.innerWidth - 16);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = window.innerHeight - rect.bottom;
  const above = rect.top;
  const style: Record<string, string> = { left: `${left}px`, width: `${width}px` };
  if (below < 220 && above > below) {
    style.bottom = `${window.innerHeight - rect.top + 4}px`;
    style.maxHeight = `${Math.min(300, above - 12)}px`;
  } else {
    style.top = `${rect.bottom + 4}px`;
    style.maxHeight = `${Math.max(140, Math.min(300, below - 12))}px`;
  }
  position.value = style;
}
function onViewportChange(event?: Event): void {
  // Le défilement de la liste elle-même ne la déplace pas.
  if (event && list.value && event.target instanceof Node && list.value.contains(event.target)) return;
  place();
}
watch(open, (value) => {
  if (typeof window === 'undefined') return;
  if (value) {
    place();
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
  } else {
    window.removeEventListener('scroll', onViewportChange, true);
    window.removeEventListener('resize', onViewportChange);
  }
});
onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', onViewportChange, true);
  window.removeEventListener('resize', onViewportChange);
});

// ---------------------------------------------------------------- Ouverture, choix, clavier
function selectedIndex(): number {
  const index = visible.value.findIndex((o) => o.value === props.modelValue);
  return index >= 0 ? index : nextActiveIndex(visible.value, -1, 1);
}
function openList(): void {
  if (props.disabled) return;
  if (!open.value) {
    open.value = true;
    active.value = selectedIndex();
    void scrollActive();
  }
}
function closeList(): void {
  open.value = false;
  editing.value = false;
  query.value = '';
  active.value = -1;
}
async function scrollActive(): Promise<void> {
  await nextTick();
  const el = active.value >= 0 ? document.getElementById(optionId(active.value)) : null;
  el?.scrollIntoView?.({ block: 'nearest' });
}
function choose(option: SearchOption): void {
  if (option.disabled) return;
  closeList();
  if (option.value !== props.modelValue) {
    emit('update:modelValue', option.value);
    emit('change', option.value);
  }
}
function clear(): void {
  closeList();
  if (props.modelValue !== null) {
    emit('update:modelValue', null);
    emit('change', null);
  }
  input.value?.focus();
}
function onInput(event: Event): void {
  query.value = (event.target as HTMLInputElement).value;
  editing.value = true;
  if (!open.value) open.value = true;
  active.value = nextActiveIndex(visible.value, -1, 1);
  void scrollActive();
}
function move(delta: 1 | -1): void {
  if (!open.value) {
    openList();
    return;
  }
  active.value = nextActiveIndex(visible.value, active.value, delta);
  void scrollActive();
}
function onKeydown(event: KeyboardEvent): void {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      move(1);
      break;
    case 'ArrowUp':
      event.preventDefault();
      move(-1);
      break;
    case 'Enter': {
      if (!open.value) return;
      // Entrée choisit l'option active, sans envoyer le formulaire.
      event.preventDefault();
      const option = visible.value[active.value];
      if (option) choose(option);
      break;
    }
    case 'Escape':
      if (!open.value && !editing.value) return;
      // Ferme la liste seulement (pas la fenêtre qui contient le champ).
      event.preventDefault();
      event.stopPropagation();
      closeList();
      break;
    case 'Tab':
      closeList();
      break;
  }
}
function onBlur(): void {
  closeList();
}
watch(
  () => props.disabled,
  (value) => {
    if (value) closeList();
  },
);
defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div ref="root" class="ss" :class="[rootAttrs.class, { open, disabled }]" :style="rootAttrs.style as never">
    <input
      :id="inputId"
      ref="input"
      v-bind="inputAttrs"
      :data-testid="testid"
      class="ss-input"
      type="text"
      role="combobox"
      autocomplete="off"
      spellcheck="false"
      aria-autocomplete="list"
      :aria-label="ariaLabel"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="listId"
      :aria-activedescendant="open && active >= 0 ? optionId(active) : undefined"
      :placeholder="placeholder"
      :disabled="disabled"
      :value="inputText"
      @input="onInput"
      @keydown="onKeydown"
      @mousedown="openList"
      @blur="onBlur"
    />
    <button
      v-if="clearable && modelValue !== null && !disabled"
      type="button"
      class="ss-clear"
      :aria-label="`Effacer : ${ariaLabel}`"
      tabindex="-1"
      @mousedown.prevent
      @click="clear"
    >
      ×
    </button>
    <span v-else class="ss-caret" aria-hidden="true">▾</span>
    <span class="sr-only" aria-live="polite">{{ status }}</span>
    <Teleport to="body">
      <ul
        v-if="open"
        :id="listId"
        ref="list"
        class="ss-list"
        role="listbox"
        :aria-label="ariaLabel"
        :style="position"
        @mousedown.prevent
      >
        <li
          v-for="(option, index) in visible"
          :id="optionId(index)"
          :key="option.value"
          role="option"
          class="ss-option"
          :class="{ active: index === active, chosen: option.value === modelValue }"
          :aria-selected="option.value === modelValue ? 'true' : 'false'"
          :aria-disabled="option.disabled ? 'true' : undefined"
          @mousemove="active = option.disabled ? active : index"
          @click="choose(option)"
        >
          <span class="ss-label">{{ option.label }}</span>
          <small v-if="option.secondary" class="ss-secondary">{{ option.secondary }}</small>
        </li>
        <li v-if="visible.length === 0" class="ss-empty" role="presentation">{{ emptyText }}</li>
        <li v-else-if="hidden > 0" class="ss-empty" role="presentation">
          {{ hidden }} autre{{ hidden > 1 ? 's' : '' }} : tape pour affiner
        </li>
      </ul>
    </Teleport>
  </div>
</template>
